import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import axios from "../api";
import { supabase } from "../supabaseClient";
import AppHeader from "../components/AppHeader";
import {
    buildCatalogResponse,
    formatMoney,
    formatMoneyFromCents,
    quoteSelection,
} from "../utils/passCatalog";
import { LEGAL_VERSION, LegalConsentGate } from "../components/LegalDocumentModal";

const PAGE_STATE_KEY = "bbp_pass_app_state_v5";
const SHARE_EMAIL_KEY = "bbp_pass_share_email_drafts_v1";

const pad2 = (n) => String(n).padStart(2, "0");
const localYMD = (d = new Date()) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const formatMDY = (ymd) => {
    if (!ymd) return "—";

    const [y, m, d] = String(ymd).split("-").map(Number);

    if (!y || !m || !d) return String(ymd);

    return `${m}/${d}/${y}`;
};

function addDaysLocal(startYmd, daysToAdd) {
    if (!startYmd) return "";
    const [y, m, d] = String(startYmd).split("-").map(Number);
    const dt = new Date(y, (m || 1) - 1, d || 1);
    dt.setDate(dt.getDate() + Number(daysToAdd || 0));
    return localYMD(dt);
}

function moneyToCentsLocal(value) {
    return Math.round(Number(value || 0) * 100);
}

function passStatusLabel(p) {
    const st = String(p?.status || "").toLowerCase();
    const today = localYMD();

    const start = String(p?.start_date || "");
    const end = String(p?.end_date || "");
    const plannedStart = String(p?.planned_start_date || "");
    const plannedEnd = String(p?.planned_end_date || "");

    if (st === "unactivated" || !start || !end) {
        if (plannedStart && today < plannedStart) return "Not Redeemable Yet";
        if (plannedEnd && today > plannedEnd) return "Trip Window Passed";
        return "Ready to Activate";
    }

    if (today < start) return "Not Redeemable";
    if (today > end) return "Expired";
    return "Active";
}

function pickCurrentAdultPass(passes) {
    if (!Array.isArray(passes) || passes.length === 0) return null;

    const today = localYMD();
    const adultPasses = passes.filter(
        (p) =>
            String(p?.holder_type || "adult").toLowerCase() !== "child" &&
            !p?.shared_access &&
            !p?.managed_outbound
    );

    const active = adultPasses.find(
        (p) => p?.start_date && p?.end_date && p.start_date <= today && today <= p.end_date
    );
    if (active) return active;

    const readyOrUpcoming = adultPasses
        .filter((p) => {
            const st = String(p?.status || "").toLowerCase();
            const plannedEnd = String(p?.planned_end_date || "");
            if (!(st === "unactivated" || !p?.start_date || !p?.end_date)) return false;
            if (plannedEnd && today > plannedEnd) return false;
            return true;
        })
        .sort((a, b) => {
            const aStart = String(a?.planned_start_date || "");
            const bStart = String(b?.planned_start_date || "");
            if (aStart && bStart && aStart !== bStart) return aStart.localeCompare(bStart);
            return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        })[0];

    if (readyOrUpcoming) return readyOrUpcoming;

    return (
        adultPasses
            .filter((p) => {
                const plannedEnd = String(p?.planned_end_date || p?.end_date || "");
                return plannedEnd && today > plannedEnd;
            })
            .sort((a, b) => String(b?.planned_end_date || b?.end_date || "").localeCompare(String(a?.planned_end_date || a?.end_date || "")))[0] || null
    );
}

function sortPassesForManager(passes = []) {
    return [...passes].sort((a, b) => {
        const priority = {
            Active: 0,
            "Ready to Activate": 1,
            "Not Redeemable Yet": 2,
            "Trip Window Passed": 3,
            Expired: 4,
        };

        const pa = priority[passStatusLabel(a)] ?? 9;
        const pb = priority[passStatusLabel(b)] ?? 9;
        if (pa !== pb) return pa - pb;

        const aStart = String(a?.planned_start_date || a?.start_date || "");
        const bStart = String(b?.planned_start_date || b?.start_date || "");
        if (aStart && bStart && aStart !== bStart) return aStart.localeCompare(bStart);

        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
}

function extractClaimToken(raw = "") {
    const value = String(raw || "").trim();
    if (!value) return "";

    try {
        if (value.includes("?") || value.startsWith("http")) {
            const url = new URL(value, window.location.origin);
            return url.searchParams.get("claim") || value;
        }
    } catch {
        return value;
    }

    return value;
}

function AppManagedPassCard({
    pass,
    index,
    isCurrent,
    onOpenPass,
    onCopy,
}) {
    const isChild = String(pass?.holder_type || "adult").toLowerCase() === "child";
    const status = passStatusLabel(pass);
    const holderLabel =
        pass.holder_name ||
        (isChild ? "Child Pass" : pass.shared_access ? `Shared Big Bum Pass ${index + 1}` : `Big Bum Pass ${index + 1}`);

    return (
        <div className={`managed-pass-card managed-pass-card--compact ${isCurrent ? "is-featured" : ""}`}>
            <div className="managed-pass-card__top">
                <div>
                    <div className="passes-web-badge">{isChild ? "Crew Pass" : pass.shared_access ? "Shared Access" : "Managed Pass"}</div>
                    <h3>{holderLabel}</h3>
                    <p className="pass-description">{pass.pass_types?.name || "Beach Bum Pass"}</p>
                </div>
                <div className="managed-pass-status">{status}</div>
            </div>

            <div className="managed-pass-meta managed-pass-meta--compact">
                <div>
                    <span>Trip Window</span>
                    <strong>{formatMDY(pass.planned_start_date)} → {formatMDY(pass.planned_end_date)}</strong>
                </div>
                <div>
                    <span>Active Window</span>
                    <strong>{pass.start_date && pass.end_date ? `${formatMDY(pass.start_date)} → ${formatMDY(pass.end_date)}` : "Activates on first scan"}</strong>
                </div>
            </div>

            <div className="managed-pass-actions managed-pass-actions--compact">
                <button className="app-btn accent" type="button" onClick={() => onOpenPass(pass)}>
                    Open Pass
                </button>
                <button className="app-btn secondary" type="button" onClick={() => onCopy(pass.id, "Pass ID")}>
                    Copy ID
                </button>
            </div>
        </div>
    );
}

function AppPassDetailModal({
    pass,
    emailValue,
    onEmailChange,
    onSendEmail,
    sending,
    onCopy,
    onClose,
}) {
    if (!pass) return null;

    const isChild = String(pass?.holder_type || "adult").toLowerCase() === "child";
    const status = passStatusLabel(pass);
    const holderLabel = pass.holder_name || (isChild ? "Child Pass" : "Adult Pass");
    const canShare = !isChild && !pass.shared_access;

    return (
        <div className="pass-modal-backdrop" role="presentation" onMouseDown={onClose}>
            <div
                className="pass-modal-card pass-modal-card--app"
                role="dialog"
                aria-modal="true"
                aria-label={`${holderLabel} details`}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <button className="pass-modal-close" type="button" onClick={onClose} aria-label="Close pass details">
                    ×
                </button>

                <div className="pass-modal-head">
                    <div>
                        <div className="passes-web-badge">{isChild ? "Crew Pass" : pass.shared_access ? "Shared Access" : "Managed Pass"}</div>
                        <h2>{holderLabel}</h2>
                        <p>{pass.pass_types?.name || "Beach Bum Pass"}</p>
                    </div>
                    <div className="managed-pass-status">{status}</div>
                </div>

                <div className="pass-modal-body">
                    <div className="pass-modal-qr-shell">
                        <QRCodeSVG value={JSON.stringify({ pass_uuid: pass.id })} size={230} includeMargin />
                        <div className="pass-modal-qr-note">Show this QR when this specific pass is being scanned.</div>
                    </div>

                    <div className="managed-pass-meta pass-modal-meta">
                        <div>
                            <span>Trip Window</span>
                            <strong>{formatMDY(pass.planned_start_date)} → {formatMDY(pass.planned_end_date)}</strong>
                        </div>
                        <div>
                            <span>Active Window</span>
                            <strong>{pass.start_date && pass.end_date ? `${formatMDY(pass.start_date)} → ${formatMDY(pass.end_date)}` : "Activates on first scan"}</strong>
                        </div>
                        <div>
                            <span>Pass ID</span>
                            <strong className="mono">{pass.id}</strong>
                        </div>
                        {isChild ? (
                            <div>
                                <span>Age</span>
                                <strong>{pass.holder_age ?? "—"}</strong>
                            </div>
                        ) : null}
                    </div>
                </div>

                {canShare ? (
                    <div className="managed-pass-share managed-pass-share--modal">
                        <div className="passes-field">
                            <label className="pass-meta-label">Email this pass claim code</label>
                            <input
                                type="email"
                                placeholder="recipient@email.com"
                                value={emailValue || ""}
                                onChange={(e) => onEmailChange(pass.id, e.target.value)}
                            />
                            <div className="passes-inline-note">
                                Once claimed, this pass belongs to the recipient account while staying viewable here.
                            </div>
                        </div>
                        <button className="app-btn secondary" type="button" disabled={sending} onClick={() => onSendEmail(pass.id)}>
                            {sending ? "Sending…" : "Send Code"}
                        </button>
                    </div>
                ) : null}

                <div className="managed-pass-actions pass-modal-actions">
                    <button className="app-btn secondary" type="button" onClick={() => onCopy(pass.id, "Pass ID")}>
                        Copy Pass ID
                    </button>

                </div>
            </div>
        </div>
    );
}

export default function PassesApp() {
    const [session, setSession] = useState(null);
    const [ready, setReady] = useState(false);
    const [passes, setPasses] = useState([]);
    const [claims, setClaims] = useState({ invites: [], child_passes: [] });
    const [catalog, setCatalog] = useState(() => buildCatalogResponse({ pass_types: [] }));
    const [loadingCatalog, setLoadingCatalog] = useState(true);
    const [claimToken, setClaimToken] = useState("");
    const [childDrafts, setChildDrafts] = useState({});
    const [shareEmailDrafts, setShareEmailDrafts] = useState({});
    const [selectedManagedPass, setSelectedManagedPass] = useState(null);
    const [legalAccepted, setLegalAccepted] = useState(false);
    const [submittingKey, setSubmittingKey] = useState("");
    const [status, setStatus] = useState("");
    const [checkoutResult, setCheckoutResult] = useState(null);
    const [showPurchaseBuilder, setShowPurchaseBuilder] = useState(false);

    const [selectedPassTypeId, setSelectedPassTypeId] = useState("");
    const [tripStartDate, setTripStartDate] = useState(localYMD());

    const [adultQty, setAdultQty] = useState(1);
    const [babyBumQty, setBabyBumQty] = useState(0);
    const [littleBumQty, setLittleBumQty] = useState(0);
    const [grommettQty, setGrommettQty] = useState(0);

    const [deliveryMode, setDeliveryMode] = useState("self");
    const [inviteEmail, setInviteEmail] = useState("");

    const location = useLocation();

    useEffect(() => {
        let alive = true;

        (async () => {
            const { data } = await supabase.auth.getSession();
            if (!alive) return;
            setSession(data?.session ?? null);
            setReady(true);
        })();

        const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
            setSession(nextSession ?? null);
        });

        return () => {
            alive = false;
            sub?.subscription?.unsubscribe?.();
        };
    }, []);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoadingCatalog(true);
            try {
                const { data } = await axios.get("/pass-types/catalog");
                if (!alive) return;
                setCatalog(buildCatalogResponse(data));
            } catch (error) {
                console.error("Pass catalog load failed", error?.response?.data || error);
            } finally {
                if (alive) setLoadingCatalog(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, []);

    const availablePasses = useMemo(() => {
        const options = [];
        if (catalog.singleDay?.id) options.push(catalog.singleDay);
        options.push(
            ...catalog.multiDay.filter((passType) => {
                const days = Number(passType?.duration_days || 0);
                return days >= 2 && days <= 10;
            })
        );
        return options.sort((a, b) => Number(a?.duration_days || 0) - Number(b?.duration_days || 0));
    }, [catalog.singleDay, catalog.multiDay]);

    useEffect(() => {
        if (!selectedPassTypeId && availablePasses[0]?.id) {
            setSelectedPassTypeId(availablePasses[0].id);
        }
    }, [availablePasses, selectedPassTypeId]);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(PAGE_STATE_KEY);
            if (!raw) return;

            const saved = JSON.parse(raw);
            const today = localYMD();

            if (saved.selectedPassTypeId) setSelectedPassTypeId(saved.selectedPassTypeId);
            if (saved.tripStartDate && saved.tripStartDate >= today) setTripStartDate(saved.tripStartDate);
            else setTripStartDate(today);

            if (typeof saved.adultQty === "number") setAdultQty(Math.min(10, Math.max(1, saved.adultQty)));
            if (typeof saved.babyBumQty === "number") setBabyBumQty(saved.babyBumQty);
            if (typeof saved.littleBumQty === "number") setLittleBumQty(saved.littleBumQty);
            if (typeof saved.grommettQty === "number") setGrommettQty(saved.grommettQty);
            if (saved.deliveryMode) setDeliveryMode(saved.deliveryMode);
            if (saved.inviteEmail) setInviteEmail(saved.inviteEmail);
            if (saved.claimToken) setClaimToken(saved.claimToken);
        } catch {
            // ignore
        }
    }, []);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(SHARE_EMAIL_KEY);
            if (raw) setShareEmailDrafts(JSON.parse(raw));
        } catch {
            // ignore
        }
    }, []);

    useEffect(() => {
        if (adultQty > 1 && deliveryMode === "invite") {
            setDeliveryMode("self");
        }
    }, [adultQty, deliveryMode]);

    useEffect(() => {
        localStorage.setItem(
            PAGE_STATE_KEY,
            JSON.stringify({
                selectedPassTypeId,
                tripStartDate,
                adultQty,
                babyBumQty,
                littleBumQty,
                grommettQty,
                deliveryMode,
                inviteEmail,
                claimToken,
            })
        );
    }, [
        selectedPassTypeId,
        tripStartDate,
        adultQty,
        babyBumQty,
        littleBumQty,
        grommettQty,
        deliveryMode,
        inviteEmail,
        claimToken,
    ]);

    useEffect(() => {
        localStorage.setItem(SHARE_EMAIL_KEY, JSON.stringify(shareEmailDrafts));
    }, [shareEmailDrafts]);

    const loadData = async () => {
        if (!session?.access_token) {
            setPasses([]);
            setClaims({ invites: [], child_passes: [] });
            return;
        }

        try {
            const [passesRes, claimsRes] = await Promise.all([
                axios.get("/passes"),
                axios.get("/pass-claims/mine"),
            ]);
            setPasses(Array.isArray(passesRes?.data) ? passesRes.data : []);
            setClaims({
                invites: Array.isArray(claimsRes?.data?.invites) ? claimsRes.data.invites : [],
                child_passes: Array.isArray(claimsRes?.data?.child_passes) ? claimsRes.data.child_passes : [],
            });
        } catch (error) {
            console.error("PassesApp load error", error?.response?.data || error);
            setStatus("Could not load your pass details right now.");
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.access_token]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const claim = params.get("claim");
        if (claim) setClaimToken(claim);
    }, [location.search]);

    useEffect(() => {
        if (!ready) return;

        const params = new URLSearchParams(location.search);
        const checkoutState = params.get("checkout");
        const sessionId = params.get("session_id");

        if (checkoutState === "cancelled") {
            setStatus("Checkout was cancelled. Your pass selection is still here whenever you are ready.");
            return;
        }

        if (checkoutState !== "success" || !sessionId) return;
        if (!session?.access_token) return;

        let alive = true;

        (async () => {
            try {
                const { data } = await axios.get(
                    `/checkout/session-status?session_id=${encodeURIComponent(sessionId)}`
                );

                if (!alive) return;

                setCheckoutResult(data);
                setStatus("Payment complete. Your pass order is ready.");
                await loadData();

                window.history.replaceState({}, "", window.location.pathname);
            } catch (error) {
                console.error("Checkout status lookup failed", error?.response?.data || error);
                if (!alive) return;
                setStatus("Payment finished, but we could not refresh your pass order yet. Please reload in a moment.");
            }
        })();

        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, session?.access_token, location.search]);

    const currentPass = useMemo(() => pickCurrentAdultPass(passes), [passes]);


    const adultPasses = useMemo(
        () => sortPassesForManager(passes.filter((p) => String(p?.holder_type || "adult").toLowerCase() !== "child")),
        [passes]
    );

    const childPasses = useMemo(
        () => sortPassesForManager(passes.filter((p) => String(p?.holder_type || "adult").toLowerCase() === "child")),
        [passes]
    );

    const managedAdultPasses = useMemo(
        () =>
            adultPasses.filter((pass) => {
                if (currentPass?.id && pass.id === currentPass.id) return false;
                if (pass.shared_access || pass.managed_outbound) return true;
                return !!String(pass.holder_name || "").trim();
            }),
        [adultPasses, currentPass?.id]
    );

    const managedPassCount = managedAdultPasses.length + childPasses.length;

    const activeManagedPass = useMemo(() => {
        if (!selectedManagedPass?.id) return null;
        return [...managedAdultPasses, ...childPasses].find((pass) => pass.id === selectedManagedPass.id) || selectedManagedPass;
    }, [selectedManagedPass, managedAdultPasses, childPasses]);

    const childSlots = useMemo(
        () => claims.invites.filter((invite) => invite.invite_kind === "child_slot" && invite.status === "pending"),
        [claims.invites]
    );

    const adultInvites = useMemo(
        () => claims.invites.filter((invite) => invite.invite_kind === "adult_invite"),
        [claims.invites]
    );

    const selectedPass = useMemo(
        () => availablePasses.find((passType) => passType.id === selectedPassTypeId) || availablePasses[0] || null,
        [availablePasses, selectedPassTypeId]
    );

    const childOptions = useMemo(() => {
        if (!selectedPass) return { baby_bum: null, little_bum: null, grommett: null };
        return catalog.childByDurationAndBand[String(selectedPass.duration_days)] || {
            baby_bum: null,
            little_bum: null,
            grommett: null,
        };
    }, [catalog.childByDurationAndBand, selectedPass]);

    const matchedBabyBumPass = childOptions.baby_bum || null;
    const matchedLittleBumPass = childOptions.little_bum || null;
    const matchedGrommettPass = childOptions.grommett || null;

    const tripEndDate = selectedPass
        ? addDaysLocal(tripStartDate, Number(selectedPass.duration_days || 1) - 1)
        : tripStartDate;

    const purchaseQuote = useMemo(() => {
        const base = quoteSelection({
            adultPassType: selectedPass,
            babyBumPassType: matchedBabyBumPass,
            babyBumQty,
            littleBumPassType: matchedLittleBumPass,
            littleBumQty,
            grommettPassType: matchedGrommettPass,
            grommettQty,
            taxRate: 0,
        });

        const safeAdultQty = Math.max(1, Number(adultQty || 1));
        const adultUnitCents = moneyToCentsLocal(selectedPass?.price || 0);
        const extraAdultCents = adultUnitCents * Math.max(0, safeAdultQty - 1);
        const subtotalCents = Number(base.subtotalCents || 0) + extraAdultCents;

        return {
            ...base,
            adultQty: safeAdultQty,
            adultUnitCents,
            adultSubtotalCents: adultUnitCents * safeAdultQty,
            subtotalCents,
            taxCents: 0,
            totalCents: subtotalCents,
        };
    }, [
        selectedPass,
        matchedBabyBumPass,
        babyBumQty,
        matchedLittleBumPass,
        littleBumQty,
        matchedGrommettPass,
        grommettQty,
        adultQty,
    ]);

    const setShareEmailForPass = (passId, value) => {
        setShareEmailDrafts((current) => ({ ...current, [passId]: value }));
    };

    const copyText = async (value, label = "Text") => {
        try {
            await navigator.clipboard.writeText(String(value || ""));
            setStatus(`${label} copied.`);
        } catch {
            setStatus(`Could not copy ${label.toLowerCase()} on this device.`);
        }
    };

    const handleClaim = async () => {
        const token = extractClaimToken(claimToken);

        if (!token) {
            setStatus("Paste the invite token or full claim link you were sent.");
            return;
        }

        if (!session?.access_token) {
            setStatus("Log in or create an account to claim this pass.");
            return;
        }

        setSubmittingKey("claim");
        setStatus("");
        try {
            await axios.post("/pass-claims/claim", { token });
            setStatus("Pass claimed. It will activate on your first scan.");
            setClaimToken("");
            await loadData();
        } catch (error) {
            console.error("Pass claim failed", error?.response?.data || error);
            setStatus(error?.response?.data?.error || "Could not claim that pass yet.");
        } finally {
            setSubmittingKey("");
        }
    };

    const beginCheckout = async () => {
        if (!session?.access_token) {
            setStatus("Log in before you purchase a pass.");
            return;
        }

        if (!selectedPass) {
            setStatus("Choose a pass length before continuing.");
            return;
        }

        if (deliveryMode === "invite" && adultQty > 1) {
            setStatus("Email transfer purchases currently support one adult pass. Use “Keep in my account” for multiple managed adult passes.");
            return;
        }

        setStatus("");
        setSubmittingKey("purchase");
        try {
            const purchaseMode = Number(selectedPass.duration_days || 1) === 1 ? "single_day" : "multi_day";

            const { data } = await axios.post("/checkout/create-session", {
                purchase_mode: purchaseMode,
                adult_pass_type_id: selectedPass.id,
                adult_qty: adultQty,
                baby_bum_pass_type_id: matchedBabyBumPass?.id || null,
                baby_bum_qty: babyBumQty,
                little_bum_pass_type_id: matchedLittleBumPass?.id || null,
                little_bum_qty: littleBumQty,
                grommett_pass_type_id: matchedGrommettPass?.id || null,
                grommett_qty: grommettQty,
                planned_start_date: tripStartDate,
                planned_end_date: tripEndDate,
                adult_delivery: deliveryMode,
                adult_invite_email: inviteEmail || null,
                legal_accepted: true,
                legal_version: LEGAL_VERSION,
                legal_accepted_at: new Date().toISOString(),
            });

            if (!data?.checkout_url) {
                throw new Error("Checkout URL missing from server response.");
            }

            window.location.assign(data.checkout_url);
        } catch (error) {
            console.error("Checkout create failed", error?.response?.data || error);
            setStatus(error?.response?.data?.error || "Could not start checkout right now.");
            setSubmittingKey("");
        }
    };

    const sendManagedPassEmail = async (passId) => {
        const to = String(shareEmailDrafts[passId] || "").trim();

        if (!to) {
            setStatus("Enter an email address before sending this pass code.");
            return;
        }

        setSubmittingKey(`share-${passId}`);
        setStatus("");

        try {
            const { data } = await axios.post(`/passes/${passId}/share-email`, { to });
            setStatus(data?.message || `Pass code sent to ${to}.`);
        } catch (error) {
            console.error("Managed pass email failed", error?.response?.data || error);
            setStatus(error?.response?.data?.error || error?.response?.data?.message || "Could not send this pass email.");
        } finally {
            setSubmittingKey("");
        }
    };

    const scrollToPassSection = (id, openBuilder = false) => {
        if (openBuilder) setShowPurchaseBuilder(true);

        requestAnimationFrame(() => {
            document.getElementById(id)?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        });
    };

    const currentPassName = currentPass?.holder_name || currentPass?.pass_types?.name || "Beach Bum Pass";
    const currentPassType = currentPass?.pass_types?.name || "Beach Bum Pass";
    const currentPassStatus = currentPass ? passStatusLabel(currentPass) : "Ready when you are";
    const currentPassTripWindow = currentPass
        ? `${formatMDY(currentPass.planned_start_date || currentPass.start_date)} → ${formatMDY(currentPass.planned_end_date || currentPass.end_date)}`
        : `${formatMDY(tripStartDate)} → ${formatMDY(tripEndDate)}`;
    const currentPassActiveWindow = currentPass?.start_date && currentPass?.end_date
        ? `${formatMDY(currentPass.start_date)} → ${formatMDY(currentPass.end_date)}`
        : currentPass
            ? "Activates on first scan"
            : "Build your first pass below";
    const totalPassCount = adultPasses.length + childPasses.length;

    const assignChildPass = async (inviteId) => {
        const draft = childDrafts[inviteId] || {};
        const childName = String(draft.name || "")
            .trim()
            .replace(/\s+/g, " ")
            .slice(0, 80);
        const rawAge = String(draft.age || "").trim();
        const childAge = Number.parseInt(rawAge, 10);

        if (!childName) {
            setStatus("Add a child name before assigning the pass.");
            return;
        }

        const invite = childSlots.find((item) => item.id === inviteId);
        const ageMin = Number.isFinite(Number(invite?.pass_types?.age_min))
            ? Number(invite.pass_types.age_min)
            : 0;
        const ageMax = Number.isFinite(Number(invite?.pass_types?.age_max))
            ? Number(invite.pass_types.age_max)
            : 17;

        if (!Number.isInteger(childAge) || childAge < ageMin || childAge > ageMax) {
            setStatus(`Add a valid age between ${ageMin} and ${ageMax}.`);
            return;
        }

        setSubmittingKey(`child-${inviteId}`);
        setStatus("");
        try {
            await axios.post(`/pass-claims/${inviteId}/assign-child`, {
                child_name: childName,
                child_age: childAge,
            });
            setStatus(`${childName}'s child pass is assigned and waiting for the parent pass to activate first.`);
            setChildDrafts((current) => ({
                ...current,
                [inviteId]: { name: "", age: "" },
            }));
            await loadData();
        } catch (error) {
            console.error("Assign child pass failed", error?.response?.data || error);
            setStatus(error?.response?.data?.error || "Could not assign that child pass.");
        } finally {
            setSubmittingKey("");
        }
    };

    if (!ready) return <div className="page-center">Loading…</div>;

    if (!session) {
        return (
            <div className="passes-page">
                <AppHeader
                    title="Keys to the Coast"
                    subtitle="Log in or create an account to claim emailed passes and manage your Beach Bum Pass crew."
                />
                <div className="passes-grid">
                    <div className="pass-card" id="claim">
                        <h2>Claim an emailed pass</h2>
                        <p className="pass-description">Paste the invite token or full claim link from your email. Then log in or create an account to complete the claim.</p>
                        <div className="passes-field">
                            <label className="pass-meta-label">Invite token or claim link</label>
                            <input
                                type="text"
                                value={claimToken}
                                onChange={(e) => setClaimToken(e.target.value)}
                                placeholder="Paste invite token or claim link"
                            />
                        </div>
                        <div className="passes-actions">
                            <button className="app-btn accent" type="button" onClick={() => window.location.assign(`/login?next=${encodeURIComponent(`/passes?claim=${extractClaimToken(claimToken)}`)}`)}>
                                Log In to Claim
                            </button>
                            <button className="app-btn secondary" type="button" onClick={() => window.location.assign(`/register?next=${encodeURIComponent(`/passes?claim=${extractClaimToken(claimToken)}`)}`)}>
                                Create Account
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="passes-page">
            <AppHeader
                title="Passes"
                subtitle="Your Beach Bum Pass, crew passes, QR codes, and checkout all in one clean spot."
            />

            <div className="passes-header">
                <h1>{currentPass ? "Your Beach Run" : "Chart Your Pass"}</h1>
                {status ? <div className="passes-toast">{status}</div> : null}
                {checkoutResult?.order ? (
                    <div className="passes-toast">
                        Payment complete. Order status: {checkoutResult.order?.status || "pending"}.
                    </div>
                ) : null}
            </div>

            <section className={`passes-app-hero ${currentPass ? "" : "passes-app-hero--empty"}`} id="featured-pass">
                <div className="passes-app-hero__body">
                    <div className="passes-app-kicker">{currentPass ? "Featured Pass" : "No Active Pass Yet"}</div>
                    <h2>{currentPass ? currentPassName : "Build your Beach Bum Pass"}</h2>
                    <p>
                        {currentPass
                            ? `${currentPassType} is ready for your beach run. Pull up the QR, manage your crew, or add another pass below.`
                            : "Choose your trip length, add crew passes, and keep everything tucked into one app-friendly pass manager."}
                    </p>

                    <div className="passes-app-hero__stats">
                        <div>
                            <span>Status</span>
                            <strong>{currentPassStatus}</strong>
                        </div>
                        <div>
                            <span>Trip Window</span>
                            <strong>{currentPassTripWindow}</strong>
                        </div>
                        <div>
                            <span>Active Window</span>
                            <strong>{currentPassActiveWindow}</strong>
                        </div>
                    </div>

                    <div className="passes-app-hero__actions">
                        {currentPass ? (
                            <>
                                <button className="app-btn accent" type="button" onClick={() => setSelectedManagedPass(currentPass)}>
                                    Open Main QR
                                </button>
                                <button className="app-btn secondary" type="button" onClick={() => copyText(currentPass.id, "Pass ID")}>
                                    Copy ID
                                </button>
                            </>
                        ) : (
                            <button className="app-btn accent" type="button" onClick={() => scrollToPassSection("buy-passes", true)}>
                                Build My Pass
                            </button>
                        )}
                    </div>
                </div>

                {currentPass ? (
                    <div className="passes-app-hero__qr-card" aria-label="Featured pass QR code">
                        <QRCodeSVG value={JSON.stringify({ pass_uuid: currentPass.id })} size={190} includeMargin />
                        <span>Scan this pass at partner check-in.</span>
                    </div>
                ) : (
                    <div className="passes-app-hero__empty-card" aria-hidden="true">
                        <div>🌊</div>
                        <strong>Ready for the coast?</strong>
                        <span>Start below and your QR will live here.</span>
                    </div>
                )}
            </section>

            <section className="passes-app-shortcuts" aria-label="Pass manager shortcuts">
                <button type="button" className="passes-app-shortcut" onClick={() => scrollToPassSection("pass-manager")}>
                    <span>All Passes</span>
                    <strong>{totalPassCount || 0}</strong>
                    <small>Adult, shared, and crew QR codes</small>
                </button>
                <button type="button" className="passes-app-shortcut" onClick={() => scrollToPassSection("child-passes")}>
                    <span>Crew Passes</span>
                    <strong>{childPasses.length + childSlots.length}</strong>
                    <small>Assign Grommet, Little Bum, and Baby Bum passes</small>
                </button>
                <button type="button" className="passes-app-shortcut" onClick={() => scrollToPassSection("claim")}>
                    <span>Claim Code</span>
                    <strong>{adultInvites.length}</strong>
                    <small>Redeem or manage emailed passes</small>
                </button>
                <button type="button" className="passes-app-shortcut is-primary" onClick={() => scrollToPassSection("buy-passes", true)}>
                    <span>New Pass</span>
                    <strong>+</strong>
                    <small>Buy for yourself or a friend</small>
                </button>
            </section>

            <div className="passes-grid passes-grid--app">
                <div className="pass-card passes-app-section-card" id="claim">
                    <h2>Redeem an emailed pass</h2>
                    <p className="pass-description">Paste an invite token or full claim link. Your pass will appear in this account after claiming.</p>
                    <div className="passes-field">
                        <label className="pass-meta-label">Invite token or claim link</label>
                        <input
                            type="text"
                            value={claimToken}
                            onChange={(e) => setClaimToken(e.target.value)}
                            placeholder="Paste invite token or claim link"
                        />
                    </div>
                    <div className="passes-actions">
                        <button className="app-btn accent" type="button" disabled={submittingKey === "claim"} onClick={handleClaim}>
                            {submittingKey === "claim" ? "Claiming…" : "Claim Pass"}
                        </button>
                    </div>
                </div>

                <div className="pass-card passes-app-section-card" id="pass-manager">
                    <h2>Crew & Shared Passes</h2>
                    <p className="pass-description">
                        Pull up every QR code attached to this account. Your featured pass stays up top while extra adult and crew passes live here.
                    </p>

                    {managedPassCount === 0 ? (
                        <p className="pass-description">Your featured pass is shown above. Additional adult and crew passes will appear here.</p>
                    ) : (
                        <div className="managed-pass-grid">
                            {managedAdultPasses.map((pass, index) => (
                                <AppManagedPassCard
                                    key={pass.id}
                                    pass={pass}
                                    index={index + 1}
                                    isCurrent={false}
                                    onOpenPass={setSelectedManagedPass}
                                    onCopy={copyText}
                                />
                            ))}
                            {childPasses.map((pass, index) => (
                                <AppManagedPassCard
                                    key={pass.id}
                                    pass={pass}
                                    index={index}
                                    isCurrent={false}
                                    onOpenPass={setSelectedManagedPass}
                                    onCopy={copyText}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {showPurchaseBuilder ? (
                    <div className="pass-card pass-card--builder" id="buy-passes">
                        <div className="passes-card-head passes-card-head--toggle">
                            <h2>Chart More Passes</h2>
                            {currentPass ? (
                                <button className="passes-app-mini-toggle" type="button" onClick={() => setShowPurchaseBuilder(false)}>
                                    Hide Form
                                </button>
                            ) : null}
                        </div>
                        <p className="pass-description">
                            Add multiple adult passes to this account, add younger crew passes, or send one adult pass by email for someone else to claim.
                        </p>

                        <div className="pass-builder-stack">
                            <div className="passes-field">
                                <label className="pass-meta-label">How many days?</label>
                                <select
                                    value={selectedPass?.id || ""}
                                    onChange={(e) => setSelectedPassTypeId(e.target.value)}
                                    disabled={loadingCatalog}
                                >
                                    {availablePasses.map((passType) => (
                                        <option key={passType.id} value={passType.id}>
                                            {passType.duration_days} day{passType.duration_days === 1 ? "" : "s"} • {formatMoney(passType.price)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="passes-field">
                                <label className="pass-meta-label">When does your voyage begin?</label>
                                <input
                                    type="date"
                                    value={tripStartDate}
                                    min={localYMD()}
                                    onChange={(e) => setTripStartDate(e.target.value)}
                                />
                                <div className="passes-inline-note">Voyage Window: {formatMDY(tripStartDate)} → {formatMDY(tripEndDate)}</div>
                            </div>

                            <div className="passes-field">
                                <label className="pass-meta-label">Big Bum Passes (16+)</label>
                                <div className="pass-counter">
                                    <button type="button" onClick={() => setAdultQty((qty) => Math.max(1, qty - 1))}>−</button>
                                    <span>{adultQty}</span>
                                    <button type="button" onClick={() => setAdultQty((qty) => Math.min(10, qty + 1))}>+</button>
                                </div>
                                <div className="passes-inline-note">
                                    Additional adult passes stay in this account and can be emailed from Pass Manager.
                                </div>
                            </div>

                            <div className="passes-field">
                                <label className="pass-meta-label">Grommet Passes (7–15)</label>
                                <div className="pass-counter">
                                    <button type="button" onClick={() => setGrommettQty((qty) => Math.max(0, qty - 1))}>−</button>
                                    <span>{grommettQty}</span>
                                    <button type="button" onClick={() => setGrommettQty((qty) => Math.min(10, qty + 1))}>+</button>
                                </div>
                                <div className="passes-inline-note">
                                    {matchedGrommettPass
                                        ? `${formatMoney(matchedGrommettPass.price)} each for matching ${selectedPass?.duration_days || "selected"}-day Grommet passes.`
                                        : "Create matching Grommet pass types in Supabase to enable this lane."}
                                </div>
                            </div>

                            <div className="passes-field">
                                <label className="pass-meta-label">Little Bum Passes (5–6)</label>
                                <div className="pass-counter">
                                    <button type="button" onClick={() => setLittleBumQty((qty) => Math.max(0, qty - 1))}>−</button>
                                    <span>{littleBumQty}</span>
                                    <button type="button" onClick={() => setLittleBumQty((qty) => Math.min(10, qty + 1))}>+</button>
                                </div>
                                <div className="passes-inline-note">
                                    {matchedLittleBumPass
                                        ? `${formatMoney(matchedLittleBumPass.price)} each for matching ${selectedPass?.duration_days || "selected"}-day Little Bum passes.`
                                        : "Create matching Little Bum pass types in Supabase to enable this lane."}
                                </div>
                            </div>

                            <div className="passes-field">
                                <label className="pass-meta-label">Baby Bum Passes (0–3)</label>
                                <div className="pass-counter">
                                    <button type="button" onClick={() => setBabyBumQty((qty) => Math.max(0, qty - 1))}>−</button>
                                    <span>{babyBumQty}</span>
                                    <button type="button" onClick={() => setBabyBumQty((qty) => Math.min(10, qty + 1))}>+</button>
                                </div>

                            </div>

                            <div className="passes-delivery-toggle">
                                <button
                                    type="button"
                                    className={deliveryMode === "self" ? "is-active" : ""}
                                    onClick={() => setDeliveryMode("self")}
                                >
                                    Keep in my account
                                </button>
                                <button
                                    type="button"
                                    className={deliveryMode === "invite" ? "is-active" : ""}
                                    disabled={adultQty > 1}
                                    onClick={() => setDeliveryMode("invite")}
                                >
                                    Send one by email
                                </button>
                            </div>

                            {deliveryMode === "invite" ? (
                                <div className="passes-field">
                                    <label className="pass-meta-label">Where should we send it?</label>
                                    <input
                                        type="email"
                                        value={inviteEmail}
                                        onChange={(e) => setInviteEmail(e.target.value)}
                                        placeholder="friend@email.com"
                                    />
                                </div>
                            ) : null}
                        </div>

                        <div className="pass-total-box pass-total-box--builder">
                            <div><span>Big Bum adult passes</span><strong>{selectedPass ? `${adultQty} × ${formatMoney(selectedPass.price)}` : "—"}</strong></div>
                            <div><span>Grommet crew</span><strong>{grommettQty > 0 && matchedGrommettPass ? `${grommettQty} × ${formatMoney(matchedGrommettPass.price)}` : "$0.00"}</strong></div>
                            <div><span>Little Bum crew</span><strong>{littleBumQty > 0 && matchedLittleBumPass ? `${littleBumQty} × ${formatMoney(matchedLittleBumPass.price)}` : "$0.00"}</strong></div>
                            <div><span>Baby Bum crew</span><strong>{babyBumQty > 0 && matchedBabyBumPass ? `${babyBumQty} × ${formatMoney(matchedBabyBumPass.price)}` : "$0.00"}</strong></div>
                            <div><span>Tax</span><strong>Calculated at checkout</strong></div>
                            <div className="is-total"><span>Pass Subtotal</span><strong>{formatMoneyFromCents(purchaseQuote.subtotalCents)}</strong></div>
                        </div>

                        <LegalConsentGate accepted={legalAccepted} onAccept={() => setLegalAccepted(true)} />

                        <div className="secure-checkout-badges" aria-label="Secure payment information">
                            <span>🔒 Secure Checkout</span>
                            <span>🛡️ PCI-Compliant Processor</span>
                            <span>💳 Card Data Not Stored by BBP</span>
                        </div>

                        <div className="passes-actions">
                            <button
                                className="app-btn accent"
                                type="button"
                                disabled={
                                    !selectedPass ||
                                    !legalAccepted ||
                                    submittingKey === "purchase" ||
                                    (babyBumQty > 0 && !matchedBabyBumPass) ||
                                    (littleBumQty > 0 && !matchedLittleBumPass) ||
                                    (grommettQty > 0 && !matchedGrommettPass)
                                }
                                onClick={beginCheckout}
                            >
                                {submittingKey === "purchase" ? "Redirecting…" : "Continue"}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="pass-card passes-app-builder-teaser" id="buy-passes">
                        <div className="passes-app-builder-teaser__icon">🎟️</div>
                        <div>
                            <h2>Need another pass?</h2>
                            <p className="pass-description">
                                Buy a new Beach Bum Pass for yourself, add crew passes, or send one adult pass to a friend by email.
                            </p>
                        </div>
                        <button className="app-btn accent" type="button" onClick={() => setShowPurchaseBuilder(true)}>
                            Pull Up Checkout Form
                        </button>
                    </div>
                )}

                <div className="pass-card passes-app-section-card" id="child-passes">
                    <h2>Child Passes & Invitations</h2>
                    <p className="pass-description">
                        Assign child passes and prepare your crew's passes before the next adventure.
                    </p>

                    {childSlots.length > 0 ? (
                        <div className="child-pass-list">
                            {childSlots.map((invite) => {
                                const draft = childDrafts[invite.id] || { name: "", age: "" };
                                const ageMin = Number.isFinite(Number(invite?.pass_types?.age_min))
                                    ? Number(invite.pass_types.age_min)
                                    : 0;
                                const ageMax = Number.isFinite(Number(invite?.pass_types?.age_max))
                                    ? Number(invite.pass_types.age_max)
                                    : 17;

                                return (
                                    <div key={invite.id} className="child-pass-item">
                                        <div>
                                            <strong>{invite.pass_types?.name || "Child Pass"}</strong>
                                            <div className="pass-option-sub">
                                                {formatMDY(invite.planned_start_date)} → {formatMDY(invite.planned_end_date)}
                                            </div>
                                        </div>

                                        <div className="child-pass-assign-grid">
                                            <input
                                                type="text"
                                                placeholder="Child name"
                                                maxLength={80}
                                                value={draft.name}
                                                onChange={(e) =>
                                                    setChildDrafts((current) => ({
                                                        ...current,
                                                        [invite.id]: {
                                                            ...(current[invite.id] || {}),
                                                            name: e.target.value,
                                                        },
                                                    }))
                                                }
                                            />
                                            <input
                                                type="number"
                                                placeholder={`Age ${ageMin}-${ageMax}`}
                                                inputMode="numeric"
                                                min={ageMin}
                                                max={ageMax}
                                                value={draft.age}
                                                onChange={(e) =>
                                                    setChildDrafts((current) => ({
                                                        ...current,
                                                        [invite.id]: {
                                                            ...(current[invite.id] || {}),
                                                            age: e.target.value,
                                                        },
                                                    }))
                                                }
                                            />
                                        </div>

                                        <button
                                            className="app-btn secondary"
                                            type="button"
                                            disabled={submittingKey === `child-${invite.id}`}
                                            onClick={() => assignChildPass(invite.id)}
                                        >
                                            {submittingKey === `child-${invite.id}` ? "Assigning…" : "Assign"}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="pass-description">No unassigned Grommet, Little Bum, or Baby Bum Passes right now.</p>
                    )}
                </div>

                <div className="pass-card passes-app-section-card">
                    <h2>Transferred Adult Passes</h2>
                    <p className="pass-description">
                        Big Bum Passes sent by checkout email are listed here until claimed.
                    </p>

                    {adultInvites.length === 0 ? (
                        <p className="pass-description">No invites yet.</p>
                    ) : (
                        <div className="invite-list">
                            {adultInvites.map((invite) => (
                                <div key={invite.id} className="invite-item">
                                    <div>
                                        <strong>{invite.pass_types?.name || "Adult Pass"}</strong>
                                        <div className="pass-option-sub">
                                            {invite.invite_email || "Invite email not set"} • {invite.status}
                                            {invite.claimed_user_pass_id ? ` • Claimed Pass: ${invite.claimed_user_pass_id}` : ""}
                                        </div>
                                    </div>
                                    {invite.claim_url && String(invite.status).toLowerCase() === "pending" ? (
                                        <button
                                            className="app-btn secondary"
                                            type="button"
                                            onClick={() => copyText(invite.claim_url, "Invite link")}
                                        >
                                            Copy Invite Link
                                        </button>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {activeManagedPass ? (
                <AppPassDetailModal
                    pass={activeManagedPass}
                    emailValue={shareEmailDrafts[activeManagedPass.id] || ""}
                    onEmailChange={setShareEmailForPass}
                    onSendEmail={sendManagedPassEmail}
                    sending={submittingKey === `share-${activeManagedPass.id}`}
                    onCopy={copyText}
                    onClose={() => setSelectedManagedPass(null)}
                />
            ) : null}
        </div>
    );
}
